import {
  ThreadsScheduleVideoArgs,
  ThreadsShareVideoArgs,
} from 'redux/middleware/api/podcast-service';
import {
  scheduleVideo,
  shareVideo,
} from 'redux/middleware/api/podcast-service/actions';
import { getSocialProfile } from 'redux/middleware/api/third-party-authentication-service/actions';
import { GetSocialProfileAction } from 'redux/middleware/api/third-party-authentication-service/types';
import { isShareStatusResolved } from 'redux/middleware/api/video-export-service';
import { getSocialShare } from 'redux/middleware/api/video-export-service/actions';
import { onThreadsVideoUploadedSuccessfully } from 'redux/modules/mixpanel';
import {
  showPostErrorNotification,
  showPostSuccessNotification,
  showScheduleSuccessNotification,
} from 'redux/modules/social/actions';
import { ThunkAction } from 'redux/types';
import { SocialSharePlatform } from 'types';
import { VIDEO_EXPORT_POLL_MAX_ATTEMPTS } from 'utils/constants';
import reduxPoll from 'utils/redux-poll';
import { Type } from './action-types';
import {
  threadsSelector,
  threadsSocialShareIdSelector,
  threadsSocialSharesSelector,
  threadsUserDataSelector,
} from './selectors';
import { ThreadsAuthorizeSuccessAction, ThreadsPostMessageData } from './types';

const THREADS_POST_ID = 'app/threads/post-poll';
const THREADS_PROFILE_ID = 'app/threads/profile-poll';

const showSuccessNotification = (): ThunkAction<void> => (
  dispatch,
  getState,
) => {
  const { sharePostUrl } = threadsSocialSharesSelector(getState());

  dispatch(
    showPostSuccessNotification({
      sharePostUrl,
    }),
  );
};

export const threadsAuthorizeSuccess = (
  data: Pick<
    ThreadsPostMessageData['tokenInfo'],
    'accessToken' | 'idToken' | 'providerUserId'
  >,
): ThreadsAuthorizeSuccessAction => ({
  type: Type.THREADS_AUTHORIZE_SUCCESS,
  payload: data,
});

export const getThreadsUser = (
  response: GetSocialProfileAction,
): ThunkAction<Promise<void>> => async dispatch => {
  dispatch({ type: Type.THREADS_USER_DATA_GET_REQUEST });

  try {
    dispatch({
      payload: response,
      type: Type.THREADS_USER_DATA_GET_SUCCESS,
    });
  } catch (error) {
    dispatch({
      error: new Error('Error getting user data'),
      type: Type.THREADS_USER_DATA_GET_FAILURE,
    });

    throw error;
  }
};

export const clearThreadsUser = () => ({
  type: Type.THREADS_USER_CLEAR,
});

export const threadsSelectUser = (newAccount: string | number) => ({
  type: Type.THREADS_USER_ID_SELECT,
  payload: newAccount,
});

export const waitForThreadsUser = (
  threadsUserId?: string,
): ThunkAction<Promise<void>> => async (dispatch, getState) => {
  dispatch({ type: Type.THREADS_USER_DATA_GET_AWAIT_REQUEST });

  try {
    await reduxPoll(
      dispatch,
      async () => {
        const {
          providerUserId = threadsUserId,
          idToken,
          accessToken,
        } = threadsUserDataSelector(getState());

        const { response } = await dispatch(
          getSocialProfile(
            SocialSharePlatform.THREADS,
            providerUserId,
            idToken,
            accessToken,
          ),
        );

        if (response) {
          dispatch(getThreadsUser(response));
        }
      },
      {
        id: THREADS_PROFILE_ID,
        intervalMillis: 3000,
        maxAttempts: VIDEO_EXPORT_POLL_MAX_ATTEMPTS,
        shouldContinue: err => {
          if (err) return false;

          const { username } = threadsSelector(getState());

          return username === undefined;
        },
      },
    );

    dispatch({
      type: Type.THREADS_USER_DATA_GET_AWAIT_SUCCESS,
    });
  } catch (err) {
    dispatch({ type: Type.THREADS_USER_DATA_GET_AWAIT_FAILURE });

    throw err;
  }
};

const waitForThreadsPost = (): ThunkAction<Promise<void>> => async (
  dispatch,
  getState,
) => {
  dispatch({ type: Type.THREADS_VIDEO_POST_AWAIT_REQUEST });

  const socialShareId = threadsSocialShareIdSelector(getState());

  try {
    await reduxPoll(dispatch, () => dispatch(getSocialShare(socialShareId)), {
      id: THREADS_POST_ID,
      intervalMillis: 3000,
      maxAttempts: VIDEO_EXPORT_POLL_MAX_ATTEMPTS,
      shouldContinue: err => {
        if (err) {
          return false;
        }

        const share = threadsSocialSharesSelector(getState());

        if (share.shareStatus === 'error') {
          throw err;
        }

        return isShareStatusResolved(share, ['uploaded']);
      },
    });

    dispatch({
      type: Type.THREADS_VIDEO_POST_AWAIT_SUCCESS,
    });
    dispatch(showSuccessNotification());
    dispatch(onThreadsVideoUploadedSuccessfully());
  } catch (err) {
    // NB: Error isn't send in the action because it is thrown and handled by
    // postThreadsVideo, which will send it to the reducer.
    dispatch({ type: Type.THREADS_VIDEO_POST_AWAIT_FAILURE });

    throw err;
  }
};

export const postThreadsVideo = (
  embedVideoId: string,
  opts: Omit<ThreadsShareVideoArgs, 'platform'>,
): ThunkAction<Promise<void>> => async (dispatch, getState) => {
  dispatch({ type: Type.THREADS_VIDEO_POST_REQUEST });

  const { accessToken } = threadsUserDataSelector(getState());

  try {
    const {
      response: { socialShareId },
    } = await dispatch(
      shareVideo(embedVideoId, accessToken, {
        ...opts,
        platform: SocialSharePlatform.THREADS,
      }),
    );

    dispatch({
      payload: { socialShareId },
      type: Type.THREADS_VIDEO_POST_SUCCESS,
    });

    await dispatch(waitForThreadsPost());
  } catch (err) {
    dispatch({
      error: err,
      type: Type.THREADS_VIDEO_POST_FAILURE,
    });

    dispatch(showPostErrorNotification());
  }
};

export const scheduleThreadsVideo = (
  embedVideoId: string,
  opts: Omit<ThreadsScheduleVideoArgs, 'platform'>,
): ThunkAction<Promise<void>> => async (dispatch, getState) => {
  const { accessToken } = threadsUserDataSelector(getState());

  try {
    await dispatch(
      scheduleVideo(embedVideoId, accessToken, {
        ...opts,
        platform: SocialSharePlatform.THREADS,
      }),
    );

    dispatch(showScheduleSuccessNotification(opts.scheduledForMillis));
  } catch (err) {
    dispatch(showPostErrorNotification());
  }
};
